import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import i18n from "../i18n";
import { dirFor } from "../utils/article";
import { SUPPORTED, FALLBACK_LANG, STORAGE_KEY, isSupported } from "../utils/lang";

const LanguageContext = createContext({
  lang: FALLBACK_LANG,
  dir: dirFor(FALLBACK_LANG),
  setLang: () => {},
  supported: SUPPORTED,
});

export const useLanguage = () => useContext(LanguageContext);

// Single source of truth for the active UI language. The language lives in the
// URL path (`/:lang/...`); the router (LangGate) guarantees `lang` is valid
// before this provider mounts. Keeps i18next, localStorage and the <html>
// lang/dir attributes in sync, and exposes a setter that swaps the path segment.
export default function LanguageProvider({ children }) {
  const { lang: rawLang } = useParams();
  const lang = isSupported(rawLang) ? rawLang : FALLBACK_LANG;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (i18n.language !== lang) i18n.changeLanguage(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch (e) {
      // ignore write failures (private mode)
    }
    document.documentElement.lang = lang;
    document.documentElement.dir = dirFor(lang);
  }, [lang]);

  const setLang = useCallback(
    (next) => {
      if (!isSupported(next) || next === lang) return;
      // Swap the leading `/:lang` segment, keep the rest of the path + query.
      const rest = location.pathname.replace(/^\/[^/]+/, "");
      navigate(`/${next}${rest}${location.search}`);
    },
    [lang, location.pathname, location.search, navigate]
  );

  const value = useMemo(
    () => ({ lang, dir: dirFor(lang), setLang, supported: SUPPORTED }),
    [lang, setLang]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}
